import React from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import { merge } from 'lodash';
import type { SchemaType } from '$util/schema';
import theme from '$util/theme/vars';
import { Card } from '$components/basic/Card';
import { Heading } from '$quantum/atoms/Heading';
import VrmLookup from '$quantum/molecules/VrmLookup';
import { CmsImageSchema, CmsShortTextSchema } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import Styled, { AntGrid, StyledPicture } from './styled';

const { Col, Row } = AntGrid;

const defaultConfiguration = {
  disableLookup: false,
};

const HomepageHeroVrmLookupSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema,
    subheading: CmsShortTextSchema.optional(),
    mainCta: CmsShortTextSchema,
    desktopImage: CmsImageSchema,
    mobileImage: CmsImageSchema,
  }),
  configuration: z
    .object({
      disableLookup: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type HomepageHeroVrmLookupTranslatedEntities = SchemaType<
  (typeof HomepageHeroVrmLookupSchema)['translatedEntities']
>;
export type HomepageHeroVrmLookupConfiguration = SchemaType<
  (typeof HomepageHeroVrmLookupSchema)['configuration']
>;

export const HomepageHeroVrmLookup: ContentComponent<
  HomepageHeroVrmLookupTranslatedEntities,
  HomepageHeroVrmLookupConfiguration
> = function HomepageHeroVrmLookup({ translatedEntities, configuration, className }) {
  const { heading, subheading, mainCta, desktopImage, mobileImage } = translatedEntities;
  const { disableLookup } = merge(defaultConfiguration, configuration);

  return (
    <Styled className={classNames(HomepageHeroVrmLookup.displayName, className)}>
      <Row className={classNames('top-wrapper main', className)}>
        <Col xs={12} md={6}>
          <Card className="vrm-lookup-card">
            <Heading>{heading.value}</Heading>
            {subheading && <p>{subheading.value}</p>}
            {!disableLookup && <VrmLookup buttonText={mainCta.value} />}
          </Card>
        </Col>
      </Row>
      <StyledPicture>
        <source srcSet={desktopImage.url} media={`(min-width: ${theme.mediaQueryBreakpoints.md}px)`} />
        <source srcSet={mobileImage.url} media={`(max-width: ${theme.mediaQueryBreakpoints.md - 1}px)`} />
        {/* NOTE: not using Image component for simpler, native responsive performance without additional DOM nodes */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={mobileImage.url}
          alt={mobileImage.alt}
          width={mobileImage.width}
          height={mobileImage.height}
          title={mobileImage.title}
        />
      </StyledPicture>
    </Styled>
  );
};

HomepageHeroVrmLookup.displayName = 'HomepageHeroVrmLookup';
HomepageHeroVrmLookup.schema = HomepageHeroVrmLookupSchema;
